import { useEffect, useRef, RefObject } from 'react';

/**
 * Shared accessibility behaviour for modal dialogs. Since #552 only
 * `ModalBackdrop` calls it: every modal uses that shell
 * (`modal-standard-552.spec.ts`).
 *
 * Responsibilities:
 *   - ESC → calls `onClose`.
 *   - Tab / Shift+Tab cycles focus within `dialogRef` (focus trap).
 *   - A stack of the open dialogs (#552). Only the TOP dialog handles Esc and
 *     Tab. Every dialog listens on the document, and stopPropagation does not
 *     stop a second listener on the same target: one Esc closed the Bout plan
 *     analysis AND the Bout plan under it, and the two traps pulled the focus
 *     between them.
 *   - While a dialog is open, the page under it does not scroll. The stack
 *     saves the overflow of the body when the first dialog opens and puts it
 *     back when the last one closes.
 *   - When active: remembers the currently-focused element and moves focus
 *     into the dialog: `[data-autofocus]`, else the first field (input,
 *     select, textarea), else the first focusable element (the close X), else
 *     the dialog. Never a header button first: the red trash of an edit modal
 *     had the focus, so Enter started a delete. The focus does not scroll the
 *     dialog. Do not use the React `autoFocus` prop in a modal: it moves the
 *     focus before this effect, and the focus then never returns to the
 *     trigger. Use `data-autofocus`.
 *   - #665 review — a disabled `[data-autofocus]` target (the confirm button
 *     of a question during its pause) cannot take the focus. The dialog holds
 *     the focus until the target turns on, and then the target gets it. Thus
 *     no key goes to the page under the dialog or to the close X in the
 *     pause. When the person moved the focus in the pause, it stays there.
 *   - When unmounted: restores focus to the previously-focused element.
 *
 * The mount and the unmount of ModalBackdrop open and close the dialog.
 */

/** The open dialogs, the newest last. Shared by every modal of the page. */
const openStack: HTMLElement[] = [];
/** The overflow of the body before the first dialog opened. */
let savedBodyOverflow = '';

export function useModalA11y(dialogRef: RefObject<HTMLElement | null>, onClose: () => void) {

  // Latch onClose in a ref. Callers commonly pass inline arrows
  // (`() => setShowX(false)`), which change identity on every parent render.
  // If onClose were in the effect deps, every parent re-render (e.g. typing
  // into a modal input that updates parent state) would tear down and re-run
  // the effect — restoring focus to the trigger, then re-focusing the first
  // dialog field, and stealing focus away from whatever the user was typing
  // into. See bug: typing in "Add Athlete Record" modal stole focus back to
  // First Name on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog: HTMLElement | null = dialogRef.current;
    if (!dialog) return;
    const dialogEl: HTMLElement = dialog;

    const prevActive = document.activeElement as HTMLElement | null;

    openStack.push(dialogEl);
    if (openStack.length === 1) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    const focusables = getFocusables(dialogEl);
    const wanted = dialogEl.querySelector<HTMLElement>('[data-autofocus]');
    const waits = !!wanted && wanted.matches(':disabled');
    const target = (waits ? dialogEl : wanted)
      ?? focusables.find(el => el.matches('input, select, textarea'))
      ?? focusables[0]
      ?? dialogEl;
    target.focus({ preventScroll: true });
    // The target turns on: it gets the focus if the dialog still holds it.
    let armWatch: MutationObserver | null = null;
    if (waits && wanted) {
      armWatch = new MutationObserver(() => {
        if (wanted.matches(':disabled')) return;
        armWatch?.disconnect();
        armWatch = null;
        if (document.activeElement === dialogEl) wanted.focus({ preventScroll: true });
      });
      armWatch.observe(wanted, { attributes: true, attributeFilter: ['disabled'] });
    }

    function onKeyDown(e: KeyboardEvent) {
      // Only the top dialog answers a key.
      if (openStack[openStack.length - 1] !== dialogEl) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      // Re-query each Tab so dynamically-added inputs (lazy-mounted fields,
      // async dropdowns) are included.
      const focusables = getFocusables(dialogEl);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      // If focus somehow escaped the dialog, pull it back.
      if (!dialogEl.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      // The dialog itself holds the focus (a pause, no focusable at open):
      // Shift+Tab must not leave it for the page.
      if (active === dialogEl) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      armWatch?.disconnect();
      document.removeEventListener('keydown', onKeyDown);
      const at = openStack.lastIndexOf(dialogEl);
      if (at !== -1) openStack.splice(at, 1);
      if (openStack.length === 0) document.body.style.overflow = savedBodyOverflow;
      // Restore focus. Guard against the element being detached (e.g. the
      // trigger button was in a now-unmounted subtree).
      if (prevActive && document.contains(prevActive) && typeof prevActive.focus === 'function') {
        prevActive.focus({ preventScroll: true });
      }
    };
  }, [dialogRef]);
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => el.offsetParent !== null || el === document.activeElement);
}
